import { createMapper, RouteSlotInput, SidebarGroup } from '@luks/ui';

import { cleanList } from '../../commons/cleanList';
import { mapParagraphAccordion } from './Accordion';
import { mapAllocation } from './Allocation';
import { mapParagraphAnchor } from './Anchor';
import { mapParagraphAnimatedNumbers } from './AnimatedNumbers';
import { mapBabysearch } from './BabyGallery';
import { mapCallToAction } from './CallToAction';
import { mapCampaignMonitorForm } from './CampaignMonitor';
import { mapCarousel } from './Carousel';
import { mapClinicsByArea } from './ClinicsByArea';
import { mapClinicsByTreatment } from './ClinicsByTreatment';
import { mapContextualEvent } from './ContextualEvent';
import { mapContextualText } from './ContextualText';
import { mapDiseaseFromLocation } from './DiseaseFromLocation';
import { mapDiseaseListing } from './DiseaseListing';
import { mapDocuments } from './Documents';
import { mapEventSearch } from './EventSearch';
import { mapEventsEmbed } from './EventsEmbed';
import { mapFileList } from './FileList';
import { mapFormattedTextWithTitle } from './FormattedTextWithTitle';
import { mapImageWithCaption } from './ImageWithCaption';
import { mapJobToolSearch } from './JobToolSearch';
import { mapLabCardListingListing } from "./LabCardListing";
import { mapLinkList } from './LinkList';
import { mapLocalVideo } from './LocalVideo';
import { mapParagraphLogoCloud } from "./LogoCloud";
import { mapMedicineNews } from './MedicineNews';
import { mapMegaTeasers } from './MegaTeasers';
import { mapNewsroomFiltered } from './NewsroomFiltered';
import { mapNewsroomFrontpage } from './NewsroomFrontpage';
import { mapNewsroomPersonnelBlock } from './NewsroomPersonnelBlock';
import {
  mapParagraphNewsroomResearch,
  mapParagraphNewsroomResearchHeader,
  ParagraphNewsroomResearchHeader,
} from './NewsroomResearch';
import { mapParagraphNewsroomTreatment } from './NewsroomTreatment';
import { mapNewsTeaser } from './NewsTeaser';
import { mapQuote } from './Quote';
import { mapSiteSearch } from './SiteSearch';
import { mapSpacer } from './Spacer';
import { mapSpecialistGallery } from './SpecialistGallery';
import { mapSpecialistSlider } from './SpecialistSlider';
import { mapTeaser } from './Teaser';
import { mapTeasers } from './Teasers';
import { mapTestimonialSlider } from './TestimonialSlider';
import { mapTiles } from './Tiles';
import { mapTimeline } from './Timeline';
import { mapWallsIo } from './WallsIo';
import { mapWebform } from './Webform';
import { mapWysiwygContent } from './Wysiwyg';
import { mapYoutubeEmbed } from './YoutubeEmbed';

export type ParagraphOptions = {
  advertCollection?: AdvertCollectionFragment;
  inSidebar?: boolean;
};

type AllParagraphsProcessed =
  | AllParagraphsFragment
  | ParagraphNewsroomResearchHeader;

const _mapParagraphs = createMapper<
  Array<AllParagraphsProcessed>,
  RouteSlotInput<typeof SidebarGroup, 'items'>,
  string,
  ParagraphOptions
>({
  ParagraphWysiwygContent: mapWysiwygContent,
  ParagraphAccordion: mapParagraphAccordion,
  ParagraphLinkList: mapLinkList,
  ParagraphTiles: mapTiles,
  ParagraphBabiesSearch: mapBabysearch,
  ParagraphSpecialistsSlider: mapSpecialistSlider,
  ParagraphListingDiseaseSearchSource: mapDiseaseListing,
  ParagraphLabCardListing: mapLabCardListingListing,
  ParagraphContextualText: mapContextualText,
  ParagraphDocuments: mapDocuments,
  ParagraphFileList: mapFileList,
  ParagraphTestimonialSlider: mapTestimonialSlider,
  ParagraphImageWithCaption: mapImageWithCaption,
  ParagraphEventsSearch: mapEventSearch,
  ParagraphQuote: mapQuote,
  ParagraphFormattedTextWithTitle: mapFormattedTextWithTitle,
  ParagraphTeasers: mapTeasers,
  ParagraphTeaser: mapTeaser,
  ParagraphMegaTeasers: mapMegaTeasers,
  ParagraphListingSpecialists: mapSpecialistGallery,
  ParagraphYoutubeEmbed: mapYoutubeEmbed,
  ParagraphVideo: mapLocalVideo,
  ParagraphImageCarousel: mapCarousel,
  ParagraphListingClinicsByArea: mapClinicsByArea,
  ParagraphListingAllocation: mapAllocation,
  ParagraphListingJobToolSearch: mapJobToolSearch,
  ParagraphListingJobList: mapJobToolSearch,
  ParagraphListingMedicineNews: mapMedicineNews,
  ParagraphListingEventsEmbed: mapEventsEmbed('byType'),
  ParagraphListingEventsEmbedByDepartment: mapEventsEmbed('byDepartment'),
  ParagraphListingEventsEmbedByTypeAndDepartment: mapEventsEmbed(
    'byTypeAndDepartment',
  ),
  ParagraphListingDiseaseFromLocation: mapDiseaseFromLocation,
  ParagraphListingLocationByTreatmentOffer: mapClinicsByTreatment,
  ParagraphListingNewsroomFrontpage: mapNewsroomFrontpage,
  ParagraphListingNewsroomFiltered: mapNewsroomFiltered,
  ParagraphAnchor: mapParagraphAnchor,
  ParagraphSpacer: mapSpacer,
  ParagraphAnimatedNumbers: mapParagraphAnimatedNumbers,
  ParagraphListingNewsroomPersonnelBlock: mapNewsroomPersonnelBlock,
  ParagraphForm: mapWebform,
  ParagraphEventForm: mapWebform,
  ParagraphContextualEvent: mapContextualEvent,
  ParagraphSiteSearch: mapSiteSearch,
  ParagraphStaticCampaignMonitorSubscribeForm: mapCampaignMonitorForm,
  ParagraphCallToAction: mapCallToAction,
  ParagraphStaticWallsBlock: mapWallsIo,
  ParagraphNewsTeaser: mapNewsTeaser,
  ParagraphListingNewsroomResearch: mapParagraphNewsroomResearch,
  ParagraphNewsroomResearchHeader: mapParagraphNewsroomResearchHeader,
  ParagraphListingNewsroomTreatment: mapParagraphNewsroomTreatment,
  ParagraphTimeline: mapTimeline,
  ParagraphLogoCloud: mapParagraphLogoCloud,
});

export const mapParagraphs: typeof _mapParagraphs = (...args) => {
  const [input, ...rest] = args;

  const processed: typeof input = [];
  input.forEach((item) => {
    if (item.__typename === 'ParagraphListingNewsroomResearch') {
      // Prepend NewsroomResearch listing with a header.
      processed.push({
        __typename: 'ParagraphNewsroomResearchHeader',
      });
    }
    processed.push(item);
  });

  return _mapParagraphs(processed.filter(cleanList), ...rest);
};
